import { MAX_WISHES_PER_WEEK, addDays, formatDateRu, targetWeekFor, weekDays } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { db } from '../sync/db.js';
import { enqueue } from '../sync/outbox.js';

/**
 * Пожелания по выходным.
 *
 * Семь крупных плиток — по дню на каждую. Отметить можно не больше двух дней:
 * это не отпуск, а просьба, и администратор всё равно решает сам.
 */

const WEEKDAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

export function WishesScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const today = new Date().toISOString().slice(0, 10);
  const weekStart = targetWeekFor(today);
  const days = weekDays(weekStart);

  const saved = useLiveQuery(
    async () =>
      user ? await db.dayOffWishes.where('userId').equals(user.id).toArray() : [],
    [user?.id],
    [],
  );

  const savedForWeek = saved.filter((wish) => days.includes(wish.date));

  const [picked, setPicked] = useState<string[] | null>(null);
  const [comment, setComment] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const selection = picked ?? savedForWeek.map((wish) => wish.date);
  const commentValue = comment ?? savedForWeek[0]?.comment ?? '';

  const toggle = (date: string): void => {
    const next = selection.includes(date)
      ? selection.filter((item) => item !== date)
      : [...selection, date];
    if (next.length > MAX_WISHES_PER_WEEK) return;
    if (navigator.vibrate) navigator.vibrate(20);
    setPicked(next);
    setSent(false);
  };

  const submit = async (): Promise<void> => {
    if (!user) return;
    await enqueue({
      type: 'DAYOFF_WISH_SUBMITTED',
      aggregateType: 'DAYOFF_WISH',
      aggregateId: `${weekStart}:${user.id}`,
      userId: user.id,
      payload: {
        weekStart,
        dates: selection,
        comment: commentValue.trim() === '' ? null : commentValue.trim(),
      },
    });
    if (navigator.vibrate) navigator.vibrate([30, 50, 30]);
    setSent(true);
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Желаемые выходные</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <p className="mb-4 text-base text-body">
        Неделя {formatDateRu(weekStart)} — {formatDateRu(addDays(weekStart, 6))}. Можно отметить
        не больше {MAX_WISHES_PER_WEEK} дней.
      </p>

      <ul className="mb-4 flex flex-col gap-touch">
        {days.map((date, index) => {
          const active = selection.includes(date);
          return (
            <li key={date}>
              <button
                type="button"
                onClick={() => toggle(date)}
                className={`flex w-full min-h-touch items-center justify-between rounded-xl px-5 py-4 text-left text-lg font-semibold ${
                  active ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
                }`}
              >
                <span>{WEEKDAYS[index]}</span>
                <span className="text-base text-body">{formatDateRu(date)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <label className="mb-4 flex flex-col gap-2 text-base">
        Комментарий (необязательно)
        <textarea
          rows={2}
          value={commentValue}
          onChange={(event) => {
            setComment(event.target.value);
            setSent(false);
          }}
          placeholder="Например: свадьба у брата"
          className="rounded-xl bg-canvas px-4 py-3 text-lg outline-none focus:ring-2 focus:ring-sky-500"
        />
      </label>

      {sent ? (
        <p className="rounded-xl bg-emerald-800 px-5 py-4 text-lg font-semibold text-white">
          Пожелания отправлены
        </p>
      ) : (
        <button
          type="button"
          onClick={() => void submit()}
          className="w-full rounded-xl bg-sky-600 px-5 py-5 text-lg font-semibold text-white active:bg-sky-700"
        >
          Отправить
        </button>
      )}

      <p className="mt-4 text-base text-dim">
        Пожелание — не гарантия. Администратор увидит его в графике и постарается учесть.
      </p>
    </div>
  );
}
